import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { strings } from './Landing.strings'
import qrTableImg from '@/assets/images/qrcode_table.webp'
import weddingImg from '@/assets/images/unsplash_wedding.webp'
import bookImg from '@/assets/images/book_unsplash.webp'
import { ButtonLink, SEOHead } from '@/ui/components'
import { Routes } from 'routes'
import { useLayoutInfo } from '@/ui/core'
import { useMemo } from 'react'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2em;
`

const Header = styled.div<{ $large: boolean }>`
  display: flex;
  flex-direction: column;
  align-self: center;
  align-items: center;
  padding: ${({ $large }) => ($large ? '48px 0' : '0 0 0 0')};
  gap: 1rem;
  max-width: 1000px;
  > p,
  h2 {
    color: ${({ theme }) => theme.colors.font.subdued};
  }
`
const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`
const Content = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`
const ImageContent = styled.img`
  max-width: 600px;
  width: 100%;
  height: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`
const DetailContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: start;
  max-width: 600px;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.surface.background};
  padding: 32px;
  > h2 {
    font-weight: bold;
  }
`
const ActionContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 48px 0;
  text-align: center;
  align-items: center;
  align-self: center;
  max-width: 1000px;

  > p {
    color: ${({ theme }) => theme.colors.font.subdued};
    /* font-size: 1.25rem; */
  }
  > a {
    margin-top: 24px;
    width: auto;
  }
`

const HeaderButton = styled(ButtonLink)`
  width: 250px;
`

export const Landing: React.FC = () => {
  const { size } = useLayoutInfo()

  const structuredData = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Party Guestbook - Digital Guestbook Service',
      description:
        'Transform the way you remember special occasions. Create digital guestbooks for weddings, birthdays, graduations, and more with QR code access.',
      url: 'https://partyguestbook.com',
      mainEntity: {
        '@type': 'SoftwareApplication',
        name: 'Party Guestbook',
        applicationCategory: 'EventApplication',
        operatingSystem: 'Web',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
        },
        featureList: [
          'QR Code Access',
          'Photo Sharing',
          'Guest Messages',
          'Digital Memory Book Creation',
          'Event Customization',
        ],
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://partyguestbook.com',
          },
        ],
      },
    }),
    [],
  )

  return (
    <>
      <SEOHead
        title="Party Guestbook - Capture Every Moment, Create Lasting Memories"
        description="Transform the way you remember special occasions. Create digital guestbooks for weddings, birthdays, graduations, and more with QR code access."
        keywords="digital guestbook, party guestbook, wedding guestbook, QR code guestbook, event memories, photo sharing, guest messages, celebration memories, wedding memories, birthday guestbook"
        canonical="https://partyguestbook.com"
        ogImage="https://partyguestbook.com/og-image.jpg"
        structuredData={structuredData}
      />
      <Container>
        <Header $large={size === 'large'}>
          <h1>
            <FormattedMessage {...strings.title} />
          </h1>
          <h2>
            <FormattedMessage {...strings.subtitle} />
          </h2>
          <p>
            <FormattedMessage {...strings.intro} />
          </p>
          <HeaderButton link={Routes.dashboard}>
            <FormattedMessage {...strings.actionButton} />
          </HeaderButton>
        </Header>
        <ContentContainer>
          <section>
            <Content>
              <ImageContent
                src={qrTableImg}
                alt="QR code placed on table for wedding guests to scan and access digital guestbook"
                loading="lazy"
              />
              <DetailContent>
                <h2>
                  <FormattedMessage {...strings.section1Heading} />
                </h2>
                <p>
                  <FormattedMessage {...strings.section1Body} />
                </p>
              </DetailContent>
            </Content>
          </section>
          <section>
            <Content>
              <DetailContent>
                <h2>
                  <FormattedMessage {...strings.section2Heading} />
                </h2>
                <p>
                  <FormattedMessage {...strings.section2Body} />
                </p>
              </DetailContent>
              <ImageContent
                src={weddingImg}
                alt="Beautiful wedding ceremony with guests celebrating - perfect for digital guestbook memories"
                loading="lazy"
              />
            </Content>
          </section>
          <section>
            <Content>
              <ImageContent
                src={bookImg}
                alt="Traditional guest book being transformed into modern digital memory book"
                loading="lazy"
              />
              <DetailContent>
                <h2>
                  <FormattedMessage {...strings.section3Heading} />
                </h2>
                <p>
                  <FormattedMessage {...strings.section3Body} />
                </p>
              </DetailContent>
            </Content>
          </section>
        </ContentContainer>
        <ActionContainer>
          <h2>
            <FormattedMessage {...strings.actionTitle} />
          </h2>
          <p>
            <FormattedMessage {...strings.actionSubtitle} />
          </p>
          <ButtonLink link={Routes.dashboard}>
            <FormattedMessage {...strings.actionButton} />
          </ButtonLink>
        </ActionContainer>
      </Container>
    </>
  )
}

// Attribute
//Photo by <a href="https://unsplash.com/@buttinoni?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash">Carlo Buttinoni</a> on <a href="https://unsplash.com/photos/people-standing-and-sitting-on-chair-during-daytime-lKCpUYu2TDk?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash">Unsplash</a>

// attribute
// Photo by <a href="https://unsplash.com/@jonasjacobsson?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash">Jonas Jacobsson</a> on <a href="https://unsplash.com/photos/bokeh-photography-of-open-book-0FRJ2SCuY4k?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash">Unsplash</a>
